import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { api } from '../api/client';

interface Props {
  url: string | null;
  caption?: string;
  /**
   * O anúncio, quando a foto é de uma peça.
   *
   * Com ele a sobreposição vai buscar as capturas reais da inspeção — os dois
   * lados da arma, com os adesivos onde o dono colou. Sem ele fica só a arte
   * do catálogo, que é o desenho da skin e não daquela peça.
   */
  commodityId?: string | null;
  /** Link steam:// para abrir a peça no jogo, quando o anúncio traz um. */
  inspectUrl?: string | null;
  onClose: () => void;
}

/** Sobreposição para ver a peça sem sair da tela. */
export function Lightbox({ url, caption, commodityId, inspectUrl, onClose }: Props) {
  const { t } = useI18n();
  const [failed, setFailed] = useState(false);
  /** As capturas do CS2. Começa com a arte do catálogo e é substituída. */
  const [fotos, setFotos] = useState<string[]>([]);
  const [atual, setAtual] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const [pendente, setPendente] = useState(false);

  const galeria = fotos.length > 0 ? fotos : url ? [url] : [];
  const foto = galeria[Math.min(atual, galeria.length - 1)] ?? null;

  useEffect(() => {
    setFailed(false);
    setFotos([]);
    setAtual(0);
    setPendente(false);
  }, [url, commodityId]);

  // As capturas custam uma chamada ao Youpin, então só saem daqui: quem abriu
  // a foto está olhando a peça, e é o único momento em que valem o custo.
  useEffect(() => {
    if (!url || !commodityId) return;
    let vivo = true;
    setBuscando(true);
    void api
      .inspection(commodityId)
      .then((r) => {
        if (!vivo) return;
        if (r.images.length > 0) setFotos(r.images);
        setPendente(r.pendente);
      })
      // Peça sem inspeção é o caso comum: fica a arte do catálogo, sem erro.
      .catch(() => undefined)
      .finally(() => {
        if (vivo) setBuscando(false);
      });
    return () => {
      vivo = false;
    };
  }, [url, commodityId]);

  const irPara = useCallback(
    (passo: number) => setAtual((i) => (galeria.length === 0 ? 0 : (i + passo + galeria.length) % galeria.length)),
    [galeria.length],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // Setas navegam a galeria: é o gesto esperado de quem está comparando
      // um lado com o outro.
      if (e.key === 'ArrowRight') irPara(1);
      if (e.key === 'ArrowLeft') irPara(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, irPara]);

  useEffect(() => {
    setFailed(false);
  }, [foto]);

  if (!url) return null;

  return (
    <div
      className="modal-bg lightbox-bg"
      style={{ zIndex: 60 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="lightbox-inner">
        <div className="lightbox-palco">
          {failed ? (
            <div style={{ color: 'var(--muted)', padding: 40 }}>{t('level3.modelImage')}</div>
          ) : (
            // A chave força a troca de imagem a reiniciar a transição; sem ela
            // o navegador reaproveita o nó e o corte fica seco.
            <img key={foto ?? ''} src={foto ?? ''} alt={caption ?? ''} onError={() => setFailed(true)} />
          )}

          {galeria.length > 1 && (
            <>
              <button className="lightbox-seta esq" onClick={() => irPara(-1)} aria-label="Anterior">
                ‹
              </button>
              <button className="lightbox-seta dir" onClick={() => irPara(1)} aria-label="Próxima">
                ›
              </button>
            </>
          )}

          {buscando && fotos.length === 0 && <span className="lightbox-carregando">carregando a inspeção…</span>}
        </div>

        {galeria.length > 1 && (
          <div className="lightbox-tiras">
            {galeria.map((g, i) => (
              <button
                key={g}
                className={`lightbox-tira ${i === atual ? 'on' : ''}`}
                onClick={() => setAtual(i)}
                aria-label={`Foto ${i + 1}`}
              >
                <img src={g} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}

        {caption && <div className="lightbox-legenda">{caption}</div>}
        {/* Dizer que a análise está em curso evita a leitura de que a peça não
            tem foto: em minutos ela tem. */}
        {pendente && fotos.length === 0 && (
          <div className="lightbox-legenda dim">O Youpin ainda está inspecionando esta peça.</div>
        )}

        <div className="lightbox-acoes">
          {inspectUrl && (
            <a className="btn inspect" href={inspectUrl} title={t('level3.inspectTitle')}>
              <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
                <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
                <line x1="16" y1="16" x2="21" y2="21" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
              Inspecionar no Jogo
            </a>
          )}
          <button className="btn ghost" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
